import React, { useState } from 'react'
import { Eye, EyeOff, Loader2, Check, AlertCircle, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const ChangePasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  })
  const { t, i18n } = useTranslation()

  // API base URL from environment
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const checks = [
      { test: password.length >= 8, message: t('changePasswordModal.requirements.length') },
      { test: /[a-z]/.test(password), message: t('changePasswordModal.requirements.lowercase') },
      { test: /[A-Z]/.test(password), message: t('changePasswordModal.requirements.uppercase') },
      { test: /\d/.test(password), message: t('changePasswordModal.requirements.number') },
      { test: /[!@#$%^&*(),.?":{}|<>]/.test(password), message: t('changePasswordModal.requirements.special') }
    ]

    const passedChecks = checks.filter(check => check.test).length
    const feedback = checks.filter(check => !check.test).map(check => check.message)

    setPasswordStrength({
      score: passedChecks,
      feedback
    })

    return passedChecks
  }

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }

    // Check password strength for new password
    if (field === 'newPassword') {
      checkPasswordStrength(value)
    }
  }

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = t('changePasswordModal.errors.currentRequired')
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = t('changePasswordModal.errors.newRequired')
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('changePasswordModal.errors.length')
    } else if (passwordStrength.score < 3) {
      newErrors.newPassword = t('changePasswordModal.errors.weak')
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('changePasswordModal.errors.confirmRequired')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('changePasswordModal.errors.mismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        throw new Error(t('changePasswordModal.messages.authRequired'))
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          new_password_confirmation: formData.confirmPassword
        })
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          throw new Error(data.message || t('changePasswordModal.messages.changeFailed'))
        }
        return
      }

      // Success
      onSuccess(t('changePasswordModal.messages.success'))
      handleClose()
      
    } catch (error) {
      console.error('Change password error:', error)
      setErrors({ general: error.message || t('changePasswordModal.messages.changeFailed') })
    } finally {
      setIsLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setShowPassword({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false
    })
    setErrors({})
    setPasswordStrength({ score: 0, feedback: [] })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              background: '#F3F4F6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Lock size={24} color="#6B7280" />
          </div>
          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '24px',
              fontWeight: 600,
              color: '#1F2937',
              margin: '0 0 8px 0',
            }}
          >
            {t('changePasswordModal.title')}
          </h2>
          <p
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '14px',
              color: '#6B7280',
              margin: 0,
            }}
          >
            {t('changePasswordModal.subtitle')}
          </p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <AlertCircle size={16} color="#DC2626" />
            <span
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#DC2626',
              }}
            >
              {errors.general}
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              {t('changePasswordModal.fields.current.label')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword.currentPassword ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.currentPassword ? '1px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                placeholder={t('changePasswordModal.fields.current.placeholder')}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('currentPassword')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                {showPassword.currentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.currentPassword && (
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#DC2626',
                  margin: '4px 0 0 0',
                }}
              >
                {errors.currentPassword}
              </p>
            )}
          </div>

          {/* New Password */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              {t('changePasswordModal.fields.new.label')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword.newPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.newPassword ? '1px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                placeholder={t('changePasswordModal.fields.new.placeholder')}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('newPassword')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                {showPassword.newPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.newPassword && (
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#DC2626',
                  margin: '4px 0 0 0',
                }}
              >
                {errors.newPassword}
              </p>
            )}

            {/* Password Strength Indicator */}
            {formData.newPassword && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      style={{
                        flex: 1,
                        height: '4px',
                        borderRadius: '2px',
                        background: passwordStrength.score >= level ? 
                          (passwordStrength.score >= 4 ? '#10B981' : 
                           passwordStrength.score >= 3 ? '#F59E0B' : '#EF4444') : '#E5E7EB'
                      }}
                    />
                  ))}
                </div>
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '12px',
                    color: passwordStrength.score >= 4 ? '#10B981' : 
                           passwordStrength.score >= 3 ? '#F59E0B' : '#EF4444',
                    margin: 0,
                  }}
                >
                  {passwordStrength.score >= 4
                    ? t('changePasswordModal.strength.strong')
                    : passwordStrength.score >= 3
                      ? t('changePasswordModal.strength.medium')
                      : t('changePasswordModal.strength.weak')}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: '32px' }}>
            <label
              style={{
                display: 'block',
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              {t('changePasswordModal.fields.confirm.label')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword.confirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.confirmPassword ? '1px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                placeholder={t('changePasswordModal.fields.confirm.placeholder')}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirmPassword')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                {showPassword.confirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#DC2626',
                  margin: '4px 0 0 0',
                }}
              >
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                flex: 1,
                padding: '12px 24px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                background: 'white',
                color: '#374151',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: '"Red Hat Display", sans-serif',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#F9FAFB'
                e.target.style.borderColor = '#9CA3AF'
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white'
                e.target.style.borderColor = '#D1D5DB'
              }}
            >
              {t('changePasswordModal.buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1,
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                background: isLoading ? '#9CA3AF' : '#8548A8',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: '"Red Hat Display", sans-serif',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#7C3AED'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#8548A8'
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  {t('changePasswordModal.buttons.saving')}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {t('changePasswordModal.buttons.submit')}
                </>
              )}
            </button>
          </div>
        </form>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  )
}

export default ChangePasswordModal
